/*
 * <gift-match> — gift quiz 2.0. One question at a time; the answers score every product (points per
 * chosen answer whose collections the product is in, more for earlier questions; a heavy penalty above
 * the chosen budget), and the best matches render in place. Scoring instead of filtering means there is always a result.
 */
if (!customElements.get('gift-match')) {
  customElements.define(
    'gift-match',
    class GiftMatch extends HTMLElement {
      connectedCallback() {
        // moving the section (theme editor) re-connects the element; bind once
        if (this.bound) return;
        this.bound = true;
        this.steps = Array.from(this.querySelectorAll('[data-step]'));
        this.bars = Array.from(this.querySelectorAll('.gift-match__progress-bar'));
        this.quiz = this.querySelector('[data-quiz]');
        this.panel = this.querySelector('[data-results-panel]');
        this.limit = parseInt(this.dataset.results, 10) || 3;
        try {
          this.products = JSON.parse(this.querySelector('[data-products]').textContent);
        } catch (e) {
          this.products = [];
        }
        this.answers = [];
        this.current = 0;

        this.querySelectorAll('[data-answer]').forEach((btn) => btn.addEventListener('click', () => this.choose(btn)));
        this.querySelectorAll('[data-back]').forEach((btn) => btn.addEventListener('click', () => this.go(this.current - 1)));
        this.querySelector('[data-restart]')?.addEventListener('click', () => this.restart());

        // ?moment=Geboorte (e.g. from the homepage chips) answers the first question and moves on
        const preset = GiftMatch.presetAnswer(this.steps[0], new URLSearchParams(window.location.search).get('moment'));
        if (preset) this.choose(preset, false);
      }

      // Exposed for testing: the first-step answer whose label matches the ?moment= value (case-insensitive)
      static presetAnswer(step, value) {
        if (!step || !value) return null;
        const wanted = value.trim().toLowerCase();
        return Array.from(step.querySelectorAll('[data-answer]')).find((btn) => (btn.dataset.label || '').trim().toLowerCase() === wanted) || null;
      }

      choose(btn, focus = true) {
        this.answers[this.current] = {
          label: btn.dataset.label,
          cols: (btn.dataset.cols || '').split(',').filter(Boolean),
          max: parseInt(btn.dataset.max, 10) || 0,
        };
        if (this.current >= this.steps.length - 1) this.showResults();
        else this.go(this.current + 1, focus);
      }

      go(index, focus = true) {
        if (index < 0 || index >= this.steps.length) return;
        this.steps[this.current].hidden = true;
        this.current = index;
        this.steps[index].hidden = false;
        this.bars.forEach((bar, i) => bar.classList.toggle('is-active', i <= index));
        if (focus) this.steps[index].querySelector('[data-answer]')?.focus({ preventScroll: true });
      }

      // Exposed for testing: [{ product, score, why }] sorted best first.
      rank(products = this.products, answers = this.answers) {
        return products
          .map((product, order) => {
            let score = 0;
            const why = [];
            answers.forEach((answer, i) => {
              if (!answer) return;
              if (answer.cols.length && answer.cols.some((col) => product.cols.includes(col))) {
                // each question outweighs all later ones together: the first (the moment) is what the gift is for
                score += 2 ** (answers.length - 1 - i);
                why.push(answer.label);
              }
              if (answer.max) score += product.price <= answer.max ? 0.5 : -10;
            });
            // ties keep the collection order (bestsellers first when that is the source)
            return { product, score, why, order };
          })
          .sort((a, b) => b.score - a.score || a.order - b.order);
      }

      // Exposed for testing: only real matches (at least one answer, within budget); when nothing
      // matches at all, the closest products so the result is never empty.
      static pick(ranked, limit) {
        const matched = ranked.filter((r) => r.why.length > 0 && r.score > 0);
        return (matched.length ? matched : ranked).slice(0, limit);
      }

      showResults() {
        const extra = this.panel.querySelector('template[data-extra]');
        const ranked = GiftMatch.pick(this.rank(), Math.max(1, this.limit - (extra ? 1 : 0)));
        const list = this.panel.querySelector('[data-results-list]');
        const template = this.panel.querySelector('template[data-card]');
        const heading = this.panel.querySelector('[data-results-heading]');
        const matched = ranked.length && ranked[0].why.length > 0;
        heading.textContent = matched ? heading.dataset.best : heading.dataset.close;
        list.replaceChildren(
          ...ranked.map(({ product, why }, i) => {
            const card = template.content.firstElementChild.cloneNode(true);
            card.querySelector('a').href = product.url;
            const img = card.querySelector('img');
            img.src = product.img;
            img.alt = product.title;
            card.querySelector('.gift-match__card-title').textContent = product.title;
            card.querySelector('.gift-match__card-price').textContent = product.priceText;
            const whyEl = card.querySelector('.gift-match__card-why');
            if (why.length) whyEl.textContent = `${whyEl.dataset.prefix} ${why.join(' · ')}`;
            else whyEl.remove();
            if (i === 0 && matched) card.querySelector('.gift-match__badge').hidden = false;
            return card;
          })
        );
        if (extra) list.append(extra.content.firstElementChild.cloneNode(true));
        this.quiz.hidden = true;
        this.panel.hidden = false;
        heading.focus({ preventScroll: true });
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      restart() {
        this.answers = [];
        this.panel.hidden = true;
        this.quiz.hidden = false;
        this.steps.forEach((step, i) => (step.hidden = i !== 0));
        this.current = 0;
        this.bars.forEach((bar, i) => bar.classList.toggle('is-active', i === 0));
        this.steps[0]?.querySelector('[data-answer]')?.focus({ preventScroll: true });
      }
    }
  );
}
